// Updated version of main.tsx integration
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import store from './app/store';
import './main.css';
import { initUpdateDetection } from './services/updateService';

// Performance imports
import { Suspense, lazy, startTransition } from 'react';
import React from 'react'

// App-specific metrics
const performanceMetrics = {
  start: performance.now(),
  firstPaint: 0,
  firstContentfulPaint: 0,
  timeToInteractive: 0
};

// Lazy load components with proper loading chunks
const AuthWrapper = lazy(() =>
  import(/* webpackChunkName: "auth" */ './security/AuthWrapper')
);
const AppThemeProvider = lazy(() =>
  import(/* webpackChunkName: "theme" */ './themes/AppThemeProvider')
    .then(module => ({ default: module.AppThemeProvider }))
);
const App = lazy(() =>
  import(/* webpackChunkName: "app-core" */ './App')
);

// Enhanced loader with meaningful visuals
const EnhancedLoader = () => (
  <div className="app-loader">
    <div className="app-loader__logo">
      <img src="/misionesLogo.svg" alt="Logo" width="60" height="60" />
    </div>
    <div className="app-loader__spinner"></div>
    <div className="app-loader__text">Inicializando aplicación...</div>
  </div>
);

// Error boundary for better user experience during failures
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean, error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to monitoring service
    console.error("Application error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-container">
          <h2>Ocurrió un problema al cargar la aplicación</h2>
          <p>Por favor, intente recargar la página. Si el problema persiste, contacte a soporte técnico.</p>
          <button
            onClick={() => window.location.reload()}
            className="error-reload-btn"
          >
            Recargar aplicación
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main application root component
const Root = () => (
  <ErrorBoundary>
    <Provider store={store}>
      <Suspense fallback={<EnhancedLoader />}>
        <AuthWrapper>
          <Suspense fallback={<EnhancedLoader />}>
            <AppThemeProvider>
              <App />
            </AppThemeProvider>
          </Suspense>
        </AuthWrapper>
      </Suspense>
    </Provider>
  </ErrorBoundary>
);

// Prefetch critical resources
const prefetchResources = () => {
  // Create prefetch for important routes/components
  const prefetchLinks = [
    '/dashboard',
    '/investments',
    '/profile'
  ];

  prefetchLinks.forEach(path => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = path;
    document.head.appendChild(link);
  });
};

// Handle orientation changes and viewport adjustments
let lastOrientation = window.orientation || 0;
let lastScreenWidth = window.screen.width;
let lastScreenHeight = window.screen.height;

const handleOrientationChange = () => {
  const currentOrientation = window.orientation || 0;
  const currentScreenWidth = window.screen.width;
  const currentScreenHeight = window.screen.height;

  // Check if orientation actually changed (by angle or screen dimensions)
  const orientationChanged = currentOrientation !== lastOrientation ||
    (currentScreenWidth !== lastScreenWidth || currentScreenHeight !== lastScreenHeight);

  if (orientationChanged) {
    lastOrientation = currentOrientation;
    lastScreenWidth = currentScreenWidth;
    lastScreenHeight = currentScreenHeight;

    // Force a reflow to ensure proper viewport handling
    document.documentElement.style.height = 'initial';
    document.body.style.height = 'initial';

    // Force re-layout for PWA
    setTimeout(() => {
      document.documentElement.style.height = '';
      document.body.style.height = '';

      // Update viewport meta tag dynamically
      const viewport = document.querySelector('meta[name=viewport]');
      if (viewport) {
        viewport.setAttribute('content',
          'width=device-width, initial-scale=1.0, viewport-fit=cover'
        );
      }

      // Force browser to recalculate layout
      window.dispatchEvent(new Event('resize'));

      // Dispatch custom orientation change event for components to listen to
      window.dispatchEvent(new CustomEvent('pwa-orientation-change', {
        detail: { orientation: currentOrientation, width: currentScreenWidth, height: currentScreenHeight }
      }));
    }, 150);
  }
};

// Add orientation change listeners
const setupOrientationHandlers = () => {
  // Modern approach - screen.orientation API
  if (screen.orientation) {
    screen.orientation.addEventListener('change', handleOrientationChange);
  }

  // Fallback for older browsers
  window.addEventListener('orientationchange', handleOrientationChange);

  // Additional resize handling for PWA
  let resizeTimeout: NodeJS.Timeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(handleOrientationChange, 200);
  });

  // Visual viewport API for better mobile handling (if available)
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleOrientationChange, 200);
    });
  }

  // Initial call to set up correct orientation
  setTimeout(handleOrientationChange, 100);
};

// Store the root reference
let rootInstance: any = null;

// Initialize the application
const initApp = () => {
  const rootElement = document.getElementById('root');

  // Execute prefetch operations
  prefetchResources();

  // Setup orientation change handlers
  setupOrientationHandlers();

  if (rootElement) {
    // Fix: Check if we already have a root instance
    if (!rootInstance) {
      // Use React 18's concurrent features
      startTransition(() => {
        rootInstance = createRoot(rootElement);
        rootInstance.render(<Root />);
      });
    } else {
      // Just re-render if we already have a root
      startTransition(() => {
        rootInstance.render(<Root />);
      });
    }

    // Record TTI (approximate)
    performanceMetrics.timeToInteractive = performance.now() - performanceMetrics.start;

    // Initialize update detection after the app is rendered
    setTimeout(() => {
      initUpdateDetection(store);
    }, 1000);

    // Send metrics to analytics (implement as needed)
    if (import.meta.env.PROD) {
      setTimeout(() => {
        console.info('Performance metrics:', performanceMetrics);
        // Implement metric reporting here
      }, 3000);
    }
  } else {
    console.error('Root element not found');
  }
};

// Execute initialization
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}